import { HttpClient, HttpContext, HttpContextToken, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';

import type { ApiRequest } from './api-request.model';
import { API_CONFIG } from './http.module';
import { LOGGING_CONTEXT } from './interceptors/logging.interceptor';

export const BYPASS_GATEWAY = new HttpContextToken(() => false);

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private readonly apiConfig = inject(API_CONFIG);
  private readonly http = inject(HttpClient);

  private defaultHeaders: HttpHeaders = new HttpHeaders({
    Accept: 'application/json'
  });
  private requests = new BehaviorSubject<number>(0);

  get isLoading$(): Observable<boolean> {
    return this.requests.asObservable().pipe(
      map((requests) => requests > 0),
      distinctUntilChanged()
    );
  }

  public decrementRequests(): void {
    this.requests.next(this.requests.value > 0 ? this.requests.value - 1 : 0);
  }

  public delete(apiRequest: ApiRequest): Observable<any> {
    return this.request(apiRequest, 'DELETE');
  }

  public get(apiRequest: ApiRequest): Observable<any> {
    return this.request(apiRequest, 'GET');
  }

  public incrementRequests(): void {
    this.requests.next(this.requests.value + 1);
  }

  public post(apiRequest: ApiRequest): Observable<any> {
    return this.request(apiRequest, 'POST');
  }

  public stream(apiRequest: ApiRequest): Observable<any> {
    const apiEndpoint = this.apiConfig.url + apiRequest.api;
    const params = this.getParams({
      ...apiRequest.searchParameters,
      'X-ACCESS': this.apiConfig.key,
      'ngsw-bypass': true
    });

    return new Observable<any>((subscriber) => {
      const eventSource = new EventSource(`${apiEndpoint}?${params.toString()}`);
      eventSource.onmessage = (ev): void => {
        subscriber.next(JSON.parse(ev.data));
      };
      eventSource.onerror = (error): void => {
        subscriber.error(error);
      };
      return (): void => eventSource.close();
    });
  }

  private request(apiRequest: ApiRequest, method: RequestMethod): Observable<any> {
    const gatewayPath = this.apiConfig.gatewayPath && !apiRequest.bypassGateway ? this.apiConfig.gatewayPath : '';
    const apiEndpoint = `${this.apiConfig.url}${gatewayPath}${apiRequest.api}`;
    const context = new HttpContext().set(BYPASS_GATEWAY, apiRequest.bypassGateway);

    if (apiRequest.loggingContext) {
      context.set(LOGGING_CONTEXT, apiRequest.loggingContext);
    }

    return this.http.request(method, apiEndpoint, {
      headers: this.getHeaders(apiRequest),
      body: apiRequest.body,
      params: apiRequest.searchParameters ? this.getParams(apiRequest.searchParameters) : undefined,
      responseType: apiRequest.responseType,
      context
    });
  }

  private getHeaders(apiRequest: ApiRequest): HttpHeaders {
    let headers: HttpHeaders = this.defaultHeaders;
    if (apiRequest.headers) {
      for (const key in apiRequest.headers) {
        if (Object.prototype.hasOwnProperty.call(apiRequest.headers, key)) {
          headers = headers.set(key, apiRequest.headers[key]);
        }
      }
    }
    return headers;
  }

  private getParams(searchParameters: { [key: string]: any }): HttpParams {
    let params: HttpParams = new HttpParams();
    for (const key in searchParameters) {
      if (Object.prototype.hasOwnProperty.call(searchParameters, key)) {
        const param = searchParameters[key];
        if (!!param && (!(Array.isArray(param) || typeof param == 'string') || param.length)) {
          params = params.set(key, searchParameters[key].toString());
        }
      }
    }
    return params;
  }
}
